export type UserRole = "Administrator" | "ProductManager" | "User";

export type CheckAuthResponse = {
  isAuthEnforced: boolean;
  isSignedIn: boolean;
  hasAccessPermission: boolean;
  backendConnectionFailed: boolean;
  userRole?: UserRole;
  userName?: string;
  availableModels: OpenAiModel[];
  availableIndexes: SearchIndex[];
};

export type OpenAiModel = {
  displayName: string;
  deploymentName: string;
  supportsTemperature: boolean;
  supportsVision: boolean;
}

export type SearchIndex = {
  id: string;
  displayName: string;
}

export type AskResponse = {
    conversationId: string;
    messageId: string;
    answer: string;
    citations: Citation[];
    error?: string;
    feedback?: Feedback;
    proposedAnswers?: string[];
};

export type Citation = {
    title: string;
    content: string;
    url?: string | null;
    id?: string | null;        
}

export type ChatMessage = {
  role: string;
  content: string;
  endTurn: boolean;
  id: string;
  date: string;
  feedback?: Feedback;
  context?: string;
  citations?: Citation[];
  proposedAnswers?: string[];
  canBeResolved?: boolean;
  attachments?: ConversationAttachment[];
  model?: string;
  isStatusMessage?: boolean;
};

export type ConversationHistory = {
  conversation_id: string;
  title: string;
  read_only: boolean;
  messages: ChatMessage[];
}

export enum Feedback {
  Positive = "positive",  
  MissingCitation = "missing_citation",
  WrongCitation = "wrong_citation",
  OutOfScope = "out_of_scope",
  InaccurateOrIrrelevant = "inaccurate_or_irrelevant",
  OtherUnhelpful = "other_unhelpful",
  HateSpeech = "hate_speech",
  Violent = "violent",
  Sexual = "sexual",
  Manipulative = "manipulative",
  OtherHarmful = "other_harmful",
}

export type ConversationRating = "helpful" | "not_helpful";

export type ChatResponse = {
    conversationId?: string;
    date: string;
    title?: string;        
    message?: ChatMessage;
    error?: string;
    searchedIndexes?: string[];
}

export type ConversationRequest = {
    conversation_id?: string;
    message: string;
    is_proposed_answer: boolean;
    requested_model?: string;
    temperature?: number;
    response_mode?: string;
    preferred_index_id?: string;
    is_retry?: boolean;
    /** The PromptKey of the slash-command template (e.g. "prd").
     *  When set, the backend resolves and injects the template content server-side.
     *  The frontend must NOT embed template content in `message`; send only the raw user text. */
    prompt_key?: string;
};

export type Conversation = {
  id: string;
  title: string;
  messages: ChatMessage[];
  date: string;
  updatedAt?: string;
  rating?: ConversationRating;
  additional_comments?: string;
  preferred_index_id?: string;
};

export type FrontEndSettings = {
  CHAT_HISTORY_ENABLED: boolean;
};

export type ConversationAttachment = {
  id: string;
  conversation_id: string;
  file_name: string;
  mime_type: string;
  file_size: number;
  was_summarized: boolean;
  uploaded_at: string;
};

// Export types
export type ExportFormat = "pdf" | "docx" | "txt";

export type ExportRequest = {
  conversation_id: string;
  format: ExportFormat;
  /** When set, exports only this specific response instead of the entire conversation. */
  message_id?: string;
};

export type ExportAllRequest = {
  conversation_ids: string[];
  format: ExportFormat;
};

export type ExportResponse = {
  download_url?: string;
  file_name?: string;
  format?: string;
  file_size?: number;
  error?: string;
};
